// Utility functions for downloading content

export interface DocumentForDownload {
  content: string;
  originalName: string;
}

// Helper function to detect RTL content
export const detectRTL = (content: string): boolean => {
  // Check for RTL HTML attributes
  if (content.includes('dir="rtl"') || content.includes("dir='rtl'")) {
    return true;
  }
  
  // Check for RTL characters (Arabic, Hebrew, etc.)
  const rtlChars = /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
  return rtlChars.test(content);
};

// Helper function to remove code block wrapper from content
export const removeCodeBlockWrapper = (content: string): string => {
  if (content.startsWith('```html\n') && content.endsWith('\n```')) {
    return content.slice(8, -4); // Remove ```html\n from start and \n``` from end
  }
  if (content.startsWith('```html') && content.endsWith('```')) {
    return content.slice(7, -3); // Remove ```html from start and ``` from end
  }
  return content;
};

// Download HTML content as a complete HTML file
export const downloadHTMLDocument = (doc: DocumentForDownload): void => {
  const cleanContent = removeCodeBlockWrapper(doc.content);
  const isRTL = detectRTL(cleanContent);
  
  const htmlContent = `<!DOCTYPE html>
<html lang="${isRTL ? 'ar' : 'en'}" dir="${isRTL ? 'rtl' : 'ltr'}">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${doc.originalName}</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
            line-height: 1.6;
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
            color: #333;
            direction: ${isRTL ? 'rtl' : 'ltr'};
            text-align: ${isRTL ? 'right' : 'left'};
        }
        
        h1, h2, h3, h4, h5, h6 {
            color: #2563eb;
            margin-top: 2em;
            margin-bottom: 1em;
        }
        
        p {
            margin-bottom: 1em;
        }
        
        ul, ol {
            margin-bottom: 1em;
            padding-${isRTL ? 'right' : 'left'}: 1.5em;
        }
        
        table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 1em;
        }
        
        table, th, td {
            border: 1px solid #ddd;
        }
        
        th, td {
            padding: 8px;
            text-align: ${isRTL ? 'right' : 'left'};
        }
        
        th {
            background-color: #f5f5f5;
            font-weight: bold;
        }
        
        @media print {
            body { 
                margin: 0; 
                padding: 15px; 
                max-width: none;
            }
            
            @page {
                margin: 1in;
            }
        }
    </style>
</head>
<body>
${cleanContent}
</body>
</html>`;

  const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  
  // Generate filename
  const filename = doc.originalName.replace(/\.[^/.]+$/, '') + '.html';
  link.download = filename;
  
  // Trigger download
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Download plain HTML content (just the HTML code)
export const downloadHTMLCode = (doc: DocumentForDownload): void => {
  const cleanContent = removeCodeBlockWrapper(doc.content);
  
  const blob = new Blob([cleanContent], { type: 'text/html;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  
  // Generate filename
  const filename = doc.originalName.replace(/\.[^/.]+$/, '') + '_code.html';
  link.download = filename;
  
  // Trigger download
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Download content as text file
export const downloadAsText = (doc: DocumentForDownload): void => {
  const cleanContent = removeCodeBlockWrapper(doc.content);
  
  const blob = new Blob([cleanContent], { type: 'text/plain;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  
  // Generate filename
  const filename = doc.originalName.replace(/\.[^/.]+$/, '') + '.txt';
  link.download = filename;
  
  // Trigger download
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Download multiple documents as a ZIP file (requires additional library)
export const downloadMultipleDocuments = async (documents: DocumentForDownload[]): Promise<void> => {
  // This would require a ZIP library like JSZip
  // For now, we'll download them individually
  documents.forEach((doc, index) => {
    setTimeout(() => {
      downloadHTMLDocument(doc);
    }, index * 100); // Small delay between downloads
  });
};